import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { de } from "date-fns/locale";
import { MuiTelInput } from "mui-tel-input";
import React from "react";
import Text from "../../components/Text/Text.jsx";
import Spacer from "../../components/Spacer/Spacer.jsx";
import Schritte from "../../components/NächsteSchritte/Schritte.jsx";
import { BACKEND_URL } from "../../backend.js";

import styles from "./Kontakt.module.css";

function Kontakt() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [lastSubmit, setLastSubmit] = React.useState("");
  const [showSchritte, setShowSchritte] = React.useState(false);

  // Prüfe Einwilligung
  const userConsent = React.useMemo(() => {
    const saved = localStorage.getItem("user_consent");
    return saved === "true";
  }, []);

  // Initialisiere das Datenobjekt aus dem LocalStorage oder mit Defaultwerten
  const [dataObject, setDataObject] = React.useState(() => {
    if (userConsent) {
      const saved = localStorage.getItem("form_data");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            ...parsed,
            date: parsed.date ? new Date(parsed.date) : null,
            time: parsed.time ? new Date(parsed.time) : null,
          };
        } catch {
          return {
            name: "",
            email: "",
            date: null,
            time: null,
            guestcount: "",
            eventtype: "privatfeier",
            cateringtype: "",
            telephone: "",
            company: "",
          };
        }
      }
    }
    return {
      name: "",
      email: "",
      date: null,
      time: null,
      guestcount: "",
      eventtype: "privatfeier",
      cateringtype: "buffet",
      telephone: "",
      company: "",
    };
  });

  // Alert States
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertType, setAlertType] = React.useState("success");
  const [alertMessage, setAlertMessage] = React.useState("");

  // XSS-Prüfung
  function isSafeInput(input) {
    const pattern =
      /<script.*?>.*?<\/script>|javascript:|onerror=|onload=|<.*?on\w=.*?>/gi;
    return !pattern.test(input);
  }

  // LocalStorage prüfen und ggf. leeren
  function checkAndClearLocalStorage() {
    try {
      const timeexpired = localStorage.getItem("timeexpired");
      if (timeexpired) {
        const expiredDate = new Date(timeexpired);
        const today = new Date();
        if (expiredDate < today) {
          // Vor dem Löschen user_consent sichern
          const userConsentValue = localStorage.getItem("user_consent");
          localStorage.clear();
          // Nach dem Löschen user_consent wiederherstellen
          if (userConsentValue !== null) {
            localStorage.setItem("user_consent", userConsentValue);
          }
        }
      }
    } catch (error) {
      console.error("Fehler beim Zugriff auf localStorage:", error);
    }
  }

  // Speichert den letzten Submit-Zeitpunkt im LocalStorage, wenn userConsent gegeben ist
  React.useEffect(() => {
    if (userConsent) {
      checkAndClearLocalStorage();
      const saved = localStorage.getItem("last_submit");
      if (saved) {
        setLastSubmit(saved);
      }
    }
  }, [userConsent]);

  // Handler für alle Felder
  const handleChange = (field, value) => {
    const updated = { ...dataObject, [field]: value };
    setDataObject(updated);
    if (userConsent) {
      localStorage.setItem("form_data", JSON.stringify(updated));
      // timeexpired auf 24h ab jetzt setzen
      const expires = new Date();
      expires.setHours(expires.getHours() + 24);
      localStorage.setItem("timeexpired", expires.toISOString());
    }
  };

  // Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();

    const {
      name,
      email,
      date,
      time,
      guestcount,
      eventtype,
      cateringtype,
      telephone,
      company,
    } = dataObject;

    // Felderprüfung: Sind alle Pflichtfelder belegt?
    if (
      !name ||
      !email ||
      !date ||
      !time ||
      !guestcount ||
      !eventtype ||
      !cateringtype ||
      !telephone
    ) {
      alert("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }

    // XSS-Prüfung
    if (
      !isSafeInput(name) ||
      !isSafeInput(email) ||
      !isSafeInput(telephone) ||
      !isSafeInput(company)
    ) {
      alert("Ungültige Eingabe erkannt. Bitte überprüfe deine Eingaben.");
      return;
    }

    // 3. Logikprüfungen für Felder
    // Gästezahl prüfen
    if (guestcount < 1) {
      alert("Die Anzahl der Gäste muss mindestens 1 sein.");
      return;
    }

    // Datum prüfen
    if (date) {
      const today = new Date();
      const selectedDate = new Date(date);

      // Datum darf nicht in der Vergangenheit liegen
      if (selectedDate < today.setHours(0, 0, 0, 0)) {
        alert("Das Datum darf nicht in der Vergangenheit liegen.");
        return;
      }

      const year = selectedDate.getFullYear();
      if (year < 1900 || year > 2100) {
        alert("Bitte gib ein gültiges Datum an.");
        return;
      }
    }

    const body = {
      name,
      email,
      date: date ? date.toISOString().split("T")[0] : "",
      time: time ? time.toTimeString().slice(0, 5) : "",
      guestcount,
      eventtype,
      cateringtype,
      telephone,
      company: "Wedding",
    };

    setIsLoading(true);

    fetch(`${BACKEND_URL}/send-form`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.ok) {
          setAlertType("success");
          setAlertMessage("Formular erfolgreich gesendet!");
          setShowSchritte(true);
          if (userConsent) {
            localStorage.removeItem("form_data");
            localStorage.removeItem("timeexpired");
            const now = new Date();
            const formatted = now.toLocaleString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
            localStorage.setItem("last_submit", formatted);
            setLastSubmit(formatted);
          }
          setDataObject({
            name: "",
            email: "",
            date: null,
            time: null,
            guestcount: "",
            eventtype: "privatfeier",
            cateringtype: "buffet",
            telephone: "",
            company: "",
          });
        } else {
          return response.json().then((data) => {
            throw new Error(data.error || "Fehler beim Senden des Formulars.");
          });
        }
      })
      .catch((err) => {
        setAlertType("error");
        setAlertMessage(err.message || "Fehler beim Senden des Formulars.");
      })
      .finally(() => {
        setIsLoading(false);
        setAlertOpen(true);
      });
  };

  return (
    <div className={styles.contactContainer}>
      <Text
        title="Kontakt aufnehmen"
        body={
          <>
            <span
              onClick={() => setShowSchritte(true)}
              style={{
                color: "var(--primary-color)",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Wie geht’s weiter?
            </span>{" "}
            Wir schauen uns deine Infos an und melden uns bald persönlich bei
            dir. Danach setzen wir uns zusammen und gehen alle Wünsche und Ideen
            durch, um deiner Traumhochzeit näher zu kommen.
          </>
        }
      />

      <Spacer space={"40px"} />
      {showSchritte && <Schritte onClose={() => setShowSchritte(false)} />}
      <form action="" onSubmit={handleSubmit}>
        <TextField
          label="Name"
          variant="outlined"
          value={dataObject.name}
          required
          onChange={(e) => handleChange("name", e.target.value)}
          className={styles.greenTextField}
        />
        <TextField
          label="E-Mail"
          variant="outlined"
          type="email"
          value={dataObject.email}
          required
          onChange={(e) => handleChange("email", e.target.value)}
          className={styles.greenTextField}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
          <DatePicker
            id="date-picker"
            label="Datum auswählen"
            value={dataObject.date}
            type="outlined"
            minDate={new Date()} // Verhindert Auswahl von vergangenen Daten
            required
            onChange={(newValue) => handleChange("date", newValue)}
          />
          <TimePicker
            id="time-picker"
            label="Uhrzeit auswählen"
            value={dataObject.time}
            onChange={(newValue) => handleChange("time", newValue)}
          />
        </LocalizationProvider>
        <TextField
          label="Anzahl der Gäste"
          variant="outlined"
          type="number"
          required
          min="1"
          value={dataObject.guestcount}
          onChange={(e) => handleChange("guestcount", e.target.value)}
          className={styles.greenTextField}
        />

        {/**
        <FormControl fullWidth className={styles.greenSelect}>
          <InputLabel id="eventtype-label">Eventart</InputLabel>
          <Select
            labelId="eventtype-label"
            id="eventtype-select"
            value={dataObject.eventtype}
            required
            label="Eventart"
            onChange={(e) => handleChange("eventtype", e.target.value)}
          >
            <MenuItem value={"privatfeier"}>Privatfeier</MenuItem>
            <MenuItem value={"firmenevent"}>Firmenevent</MenuItem>
            <MenuItem value={"festival"}>Festival</MenuItem>
            <MenuItem value={"sonstige"}>Sonstiges</MenuItem>
          </Select>
        </FormControl> */}
        
        {/**
        <FormLabel style={{ marginBottom: "-18px" }}>
          Hast du eine Location?
        </FormLabel>
        <FormControl className={styles.greenRadio}>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={dataObject.locationtype}
            onChange={(e) => handleChange("locationtype", e.target.value)}
          >
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Ja, ich habe bereits eine Location"
            />
            <FormControlLabel value="false" control={<Radio />} label="Nein" />
          </RadioGroup>
        </FormControl>*/}
        
        <FormLabel style={{ marginBottom: "-18px" }}>
          Catering Optionen
        </FormLabel>
        <FormControl className={styles.greenRadio}>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={dataObject.cateringtype}
            onChange={(e) => handleChange("cateringtype", e.target.value)}
          >
            <FormControlLabel
              value="buffet"
              control={<Radio />}
              label="Buffet"
            />
            <FormControlLabel value="menue" control={<Radio />} label="Service am Platz" />
            <FormControlLabel value="foodtruck" control={<Radio />} label="Foodtruck" />
          </RadioGroup>
        </FormControl>
        <MuiTelInput
          value={dataObject.telephone}
          defaultCountry="DE"
          label="Telefonnummer"
          variant="outlined"
          onChange={(newValue) => handleChange("telephone", newValue)}
          className={styles.greenTextField}
        />
        {/** 
        <TextField
          label="Firma"
          variant="outlined"
          required
          value={dataObject.company}
          onChange={(e) => handleChange("company", e.target.value)}
          className={styles.greenTextField}
        /> */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: "var(--primary-color)",
            color: "#fff",
            padding: "10px 20px",
            borderRadius: "5px",
            fontWeight: 500,
            textAlign: "center",
            fontSize: "16px",
            border: "none",
            boxShadow: "none",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "var(--primary-color-hover)",
              border: "none",
              boxShadow: "none",
              color: "#fff",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Abschicken"
          )}
        </Button>
        {lastSubmit && (
          <p className={styles.sentMessage}>
            Zuletzt abgesendet am {lastSubmit}.
          </p>
        )}
        <div
          style={{
            color: "#888",
            fontSize: "13px",
            textAlign: "center",
            marginTop: "18px",
            width: "100%",
            lineHeight: 1.5,
          }}
        >
          Indem du das Formular abschickst, erklärst du dich damit
          einverstanden, dass wir deine angegebenen Daten verarbeiten und dich
          kontaktieren.
          <br />
          Lies dir unsere{" "}
          <a
            href="/datenschutzerklaerung"
            style={{ color: "#888", textDecoration: "underline" }}
          >
            Datenschutzerklärung
          </a>{" "}
          und unsere{" "}
          <a href="/agb" style={{ color: "#888", textDecoration: "underline" }}>
            allgemeinen Geschäftsbedingungen
          </a>{" "}
          für weitere Informationen durch.
        </div>
      </form>
    </div>
  );
}

export default Kontakt;
